import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createTranslator,
  DEFAULT_LOCALE,
  type Locale,
  type TranslateParams,
  type Translator,
} from '@assist/i18n';
import { endpoints, type EntitlementsResponse, type MeResponse, type NavResponse } from './api.js';

/**
 * Sesi konsol — identitas, navigasi, entitlement, dan bahasa.
 *
 * Tiga hal yang membuat ini bukan sekadar "context React biasa":
 *
 *   1. Navigasi TIDAK ada di kode. `nav` datang dari `GET /v1/me/nav`, yang
 *      disusun Registry dari manifest modul (peta §7).
 *   2. Entitlement adalah SATU sumber kebenaran, dibaca dari
 *      `GET /v1/me/entitlements`, dipakai layar untuk memutuskan apakah sebuah
 *      layar dirender atau diganti layar "Modul di luar paket" (`70:1007`).
 *   3. Bahasa disimpan di PROFIL, bukan peramban. Menggantinya memanggil
 *      `PATCH /v1/me` dan menukar perender ICU — TANPA memuat ulang halaman dan
 *      TANPA menghilangkan isian yang belum disimpan (peta §7).
 */

export interface SessionState {
  status: 'loading' | 'anonymous' | 'authenticated' | 'error';
  me: MeResponse | null;
  nav: NavResponse | null;
  entitlements: EntitlementsResponse | null;
  locale: Locale;
  /**
   * Perender kalimat. Diekspos sebagai FUNGSI, bukan objek `Translator`, supaya
   * layar menulis `t('findings.title')` alih-alih `translator.t(...)` — bentuk
   * yang sama dengan pustaka i18n mana pun, sehingga tidak ada yang perlu
   * mengingat kekhususan ASSIST di setiap baris.
   */
  t: (key: string, params?: TranslateParams) => string;
  translator: Translator;
  errorCode: string | null;
  refresh: () => Promise<void>;
  setLocale: (locale: Locale) => Promise<void>;
  signOut: () => Promise<void>;
  /** Benar bila modul ter-entitlement ATAU inti (yang tidak dapat dimatikan). */
  hasModule: (code: string) => boolean;
}

/**
 * DIEKSPOR supaya uji dapat merender kerangka konsol dengan sesi buatan.
 *
 * Tanpa ini, `Shell` tidak dapat diuji sama sekali: ia memanggil `useSession()` yang
 * melempar di luar penyedia, dan satu-satunya jalan lain adalah menyalakan API di dalam
 * uji unit. Sebuah komponen yang ada di 28 layar dan tidak dapat diuji adalah komponen
 * yang cacatnya hanya ditemukan pemilik proyek — dan itu persis yang terjadi pada 28
 * September 2026.
 */
export const SessionContext = createContext<SessionState | null>(null);

function normaliseLocale(value: string | null | undefined): Locale {
  return value === 'id-ID' || value === 'en-US' ? value : DEFAULT_LOCALE;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [nav, setNav] = useState<NavResponse | null>(null);
  const [entitlements, setEntitlements] = useState<EntitlementsResponse | null>(null);
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    setErrorCode(null);
    try {
      const profile = await endpoints.me();
      const [navPayload, ent] = await Promise.all([endpoints.nav(), endpoints.entitlements()]);
      setMe(profile);
      setNav(navPayload);
      setEntitlements(ent);
      setLocaleState(normaliseLocale(profile.locale));
      setStatus('authenticated');
    } catch (err) {
      const code = (err as { problem?: { code?: string } }).problem?.code ?? 'INTERNAL';
      if (['UNAUTHENTICATED', 'SESSION_EXPIRED', 'SESSION_REVOKED', 'SESSION_UNKNOWN'].includes(code)) {
        setMe(null);
        setNav(null);
        setEntitlements(null);
        setStatus('anonymous');
      } else {
        setErrorCode(code);
        setStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setLocale = useCallback(async (next: Locale) => {
    // Optimistis: bahasa berganti SEKETIKA di layar. Isian yang belum disimpan
    // tidak tersentuh karena tidak ada yang dimuat ulang — hanya perender ICU
    // yang ditukar (peta §7).
    setLocaleState(next);
    try {
      await endpoints.setLocale(next);
    } catch {
      // Profil gagal disimpan: layar tetap berganti bahasa untuk sesi ini.
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await endpoints.signOut();
    } finally {
      setMe(null);
      setNav(null);
      setEntitlements(null);
      setStatus('anonymous');
    }
  }, []);

  const translator = useMemo(
    () =>
      createTranslator(locale, (key) => {
        // Kunci hilang jatuh ke bahasa Inggris dan DICATAT — tidak pernah kosong,
        // tidak pernah kunci mentah (token `locale`).
        console.warn(`[i18n] kunci hilang untuk ${locale}: ${key}`);
      }),
    [locale],
  );

  const t = useMemo(
    () => (key: string, params?: TranslateParams) => translator.t(key, params),
    [translator],
  );

  const hasModule = useCallback(
    (code: string) => {
      const row = entitlements?.modules.find((m) => m.module_code === code);
      return row ? row.is_core || row.state === 'ENTITLED' : false;
    },
    [entitlements],
  );

  const value = useMemo<SessionState>(
    () => ({
      status,
      me,
      nav,
      entitlements,
      locale,
      t,
      translator,
      errorCode,
      refresh,
      setLocale,
      signOut,
      hasModule,
    }),
    [
      status,
      me,
      nav,
      entitlements,
      locale,
      t,
      translator,
      errorCode,
      refresh,
      setLocale,
      signOut,
      hasModule,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession dipakai di luar SessionProvider');
  return ctx;
}
